<template>
  <v-container fluid class="px-12 pt-8 pb-2">
    <breadcrumbs></breadcrumbs>
    <h1>{{ id ? "Edit" : "Create" }} Answer</h1>
    <v-checkbox
      :disabled="!!questionIsDeprecated"
      @click="setDeprecationStatus(id)"
      label="Deprecate Answer"
      v-if="id"
      v-model="record.isDeprecated"
    ></v-checkbox>
    <v-alert class="mt-3" v-if="record.isDeprecated" type="warning">
      This answer has been deprecated and cannot be edited in a deprecated
      state. It can be un-deprecated only if the question with which it has been
      associated is also not deprecated.
      <span v-if="questionIsDeprecated"
        >In this case, the question has been deprecated.</span
      >
    </v-alert>
    <div v-if="questionText">
      <label class="och-label caption"
        >Question <span v-if="questionIsDeprecated">(deprecated)</span></label
      >
      <v-alert
        dense
        small
        border="left"
        color="accent"
        colored-border
        class="mt-2 pl-6"
      >
        <v-btn
          @click="
            $router.push({
              name: 'ochinUserSecurityQuestionForm',
              params: { questionId },
            })
          "
          text
          >{{ questionText }}</v-btn
        >
      </v-alert>
    </div>

    <v-row justify="center" v-if="showForm">
      <v-col cols="12">
        <v-form @submit.prevent="onSubmit" v-model="valid" class="mt-5">
          <label class="och-label">Answer Text</label>
          <v-text-field v-model="text" outlined counter="255"></v-text-field>
          <label class="och-label">Answer Help Text</label>
          <v-textarea
            clearable
            counter="1000"
            outlined
            v-model="helpText"
          ></v-textarea>
        </v-form>

        <div class="text-right mt-5">
          <v-btn v-if="id" @click="resetForm" outlined class="mr-2"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!valid"
            @click="save"
            color="primary"
            class="elevation-0"
            >Save</v-btn
          >
        </div>
      </v-col>
    </v-row>
    <div v-else>
      <v-divider class="mt-5 mb-3"></v-divider>
      <v-row>
        <v-col cols="9">
          <label class="och-label caption">Answer text</label>
          <v-alert
            dense
            small
            border="left"
            color="secondary"
            colored-border
            class="mt-2 pl-6"
          >
            <v-btn :disabled="!!record.isDeprecated" @click="edit" text>
              {{ record.text }}</v-btn
            >
          </v-alert>
        </v-col>
        <v-col cols="3" class="text-right mt-10">
          <v-btn
            :disabled="!!record.isDeprecated"
            color="primary"
            outlined
            @click="edit"
            >Edit</v-btn
          >
        </v-col>
        <v-col cols="9">
          <div>{{ record.helpText }}</div>
        </v-col>
      </v-row>
      <v-divider class="mt-3 mb-5"></v-divider>

      <v-row class="mt-10">
        <v-col cols="9"><div class="title">Child Questions</div></v-col>
        <v-col cols="3" class="text-right"
          ><v-btn
            :disabled="!!questionIsDeprecated || !!record.isDeprecated"
            @click.stop="
              $router.push({
                name: 'ochinUserSecurityQuestionCreate',
                query: { parentAnswerId: id },
              })
            "
            class="mr-3"
            color="primary"
            outlined
            >Add</v-btn
          ></v-col
        >
      </v-row>
      <child-question-table :parentAnswerId="id"></child-question-table>

      <div class="title mt-10">Security Functions</div>
      <v-autocomplete
        :disabled="!!questionIsDeprecated || !!record.isDeprecated"
        :items="unassociatedFunctionList"
        :search-input.sync="search"
        class="mt-3 mb-5"
        clearable
        hide-details
        item-text="name"
        no-data-text="No more functions found to associate"
        outlined
        placeholder="Search for a default function to associate with primary role..."
        return-object
        v-model="selectedFunction"
      >
        <template v-slot:append-outer>
          <div class="mt-n1 ml-4">
            <v-btn
              color="primary"
              outlined
              @click="associateFunction(selectedFunction)"
              :disabled="!selectedFunction"
              >Add</v-btn
            >
          </div>
        </template>
      </v-autocomplete>
      <function-table
        :hideSearch="true"
        :list="associatedFunctionList"
        @remove="remove"
      ></function-table>
    </div>
  </v-container>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Breadcrumbs from "../../Breadcrumbs";
import ChildQuestionTable from "./ChildQuestionTable";
import FunctionTable from "./AnswerFunctionTable";

export default {
  components: {
    Breadcrumbs,
    ChildQuestionTable,
    FunctionTable,
  },
  computed: {
    associatedFunctionList() {
      const answerFunctionList = this.filterAnswerFunctionList(
        "secAnswerId",
        this.id
      );

      // Takes each associated function and adds in the name of it
      const list = answerFunctionList.map(item => {
        if (!this.functionList) return;

        const match = this.functionList.find(func => {
          return func.id === item.secFunctionId;
        });

        if (!match) return;
        return Object.assign({}, item, { name: match.name });
      });

      return list.sort((a, b) => (a.name > b.name ? 1 : 0));
    },
    id() {
      return parseInt(this.$route.params.answerId, 10);
    },
    questionId() {
      return parseInt(this.$route.params.questionId);
    },
    questionIsDeprecated() {
      const question = this.getQuestionById(this.questionId);
      if (question) {
        return question.isDeprecated;
      }
      return null;
    },
    questionText() {
      const question = this.getQuestionById(this.questionId);
      if (question) {
        return question.text;
      }
      return null;
    },
    unassociatedFunctionList() {
      if (this.associatedFunctionList.length) return this.functionList;

      const filteredIdList = this.associatedFunctionList.map(item => {
        return item.secFunctionId;
      });
      return this.functionList
        .filter(item => !filteredIdList.includes(item.id))
        .sort((a, b) => (a.name > b.name ? 1 : 0));
    },
    ...mapGetters({
      filterAnswerList: "secAnswer/filterByAttributeValue",
      filterAnswerFunctionList: "secAnswerSecFunction/filterByAttributeValue",
      filterQuestionList: "secQuestion/filterByAttributeValue",
      getQuestionById: "secQuestion/getById",
    }),
    ...mapState({
      functionList: state => state.secFunction.list,
      questionList: state => state.secQuestion.list,
    }),
  },
  async created() {
    if (this.id) {
      let [answer] = await Promise.all([
        this.getAnswer(this.id),
        this.getAnswerList(),
        this.getAnswerFunctionList(),
        this.getFunctionList(),
        this.getQuestionList(),
      ]);
      this.record = answer;
    } else {
      this.showForm = true;
    }
  },
  data() {
    return {
      helpText: null,
      record: {},
      search: null,
      selectedFunction: null,
      showForm: false,
      text: null,
      valid: false,
    };
  },
  methods: {
    async associateFunction(secFunction) {
      if (!secFunction) return;
      await this.addFunction({
        secFunctionId: secFunction.id,
        secAnswerId: this.id,
      });
      this.selectedFunction = null;
    },
    edit() {
      this.helpText = this.record.helpText;
      this.text = this.record.text;
      this.showForm = true;
    },
    async remove({ id: answerFunction = null }) {
      if (!answerFunction) return;
      await this.removeAnswerFunction(answerFunction);
    },
    resetForm() {
      this.showForm = false;
    },
    async save() {
      if (!this.helpText || this.helpText.trim().length === 0) {
        this.helpText = null;
      }
      if (this.id) {
        await this.updateAnswer([
          this.id,
          { text: this.text, helpText: this.helpText },
        ]);
        this.showForm = false;
        const answer = await this.getAnswer(this.id);
        this.record = answer;
        return;
      } else {
        const result = await this.createAnswer({
          helpText: this.helpText,
          secQuestionId: this.questionId,
          text: this.text,
        });

        this.$router.push({
          name: "ochinUserSecurityAnswerForm",
          params: { answerId: result.id },
        });
        this.resetForm();
        return;
      }
    },
    async setDeprecationStatus(id) {
      // Find all child questions and deprecate recursively.
      const childQuestionList = this.filterQuestionList("parentAnswerId", id);
      for (let question of childQuestionList) {
        try {
          if (this.record.isDeprecated) {
            await this.updateDeprecationStatus(question.id, false);
          } else {
            await this.updateDeprecationStatus(question.id, true);
          }
        } catch (e) {
          console.error(e);
          this.$notifyError({ message: "Unable to update child questions." });
        }
      }
      // Set the `isDeprecated` value to the opposite of its current state.
      const isDeprecated = this.record.isDeprecated ? 0 : 1;
      try {
        await this.updateAnswer([id, { isDeprecated }]);
        this.record = await this.getAnswer(id);
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Unable to update answer." });
      }
    },
    async updateDeprecationStatus(id, deprecate = false) {
      // Iterate over children and deprecate all child questions and answers recursively.
      const answerList = this.filterAnswerList("secQuestionId", id);
      const isDeprecated = deprecate ? 1 : 0;

      let result = [];
      for (let answer of answerList) {
        const childQuestionList = this.filterQuestionList(
          "parentAnswerId",
          answer.id
        );
        for (let question of childQuestionList) {
          // Recursively deprecating child questions.
          const returnVal = await this.updateDeprecationStatus(
            question.id,
            deprecate
          );
          result = result.concat(returnVal);
        }

        this.updateAnswer([answer.id, { isDeprecated }]);
      }

      await this.updateQuestion([id, { isDeprecated }]);
      result.push(id);

      // The returned result is an array of question and child question ids.
      return result;
    },
    ...mapActions({
      addFunction: "secAnswerSecFunction/create",
      createAnswer: "secAnswer/create",
      getAnswer: "secAnswer/get",
      getAnswerList: "secAnswer/list",
      getAnswerFunctionList: "secAnswerSecFunction/list",
      getFunctionList: "secFunction/list",
      getQuestionList: "secQuestion/list",
      removeAnswerFunction: "secAnswerSecFunction/remove",
      updateAnswer: "secAnswer/update",
      updateQuestion: "secQuestion/update",
    }),
  },
};
</script>
